/**
 * Plain-text <-> block conversion for the AutoLinkedin editor.
 *
 * AutoLinkedin stores its content as plain text with bullet characters, not
 * HTML, so this is the format the API sees. The logic here is a straight port
 * of the previous draft-js implementation and the output is intentionally
 * byte-identical — the only change is that it no longer needs draft-js to
 * split text into blocks. See utils/__tests__/draftjs.test.ts.
 */

export type BlockType = string

export interface ContentBlockish {
  text: string
  type: BlockType
  depth: number
}

export interface RawContentish {
  blocks: ContentBlockish[]
}

const bulletSymbols = Object.freeze(['■', '▪', '▫', '●', '•', '◦', '⁃', '◆', '◇'])
const nestedBulletSymbolsMap = Object.freeze({
  0: '•',
  1: '◦',
  2: '▪',
} as Record<number, string>)

const generateOffset = (n: number) => {
  let result = ''
  for (let i = 0; i < n; i++) {
    result += '  '
  }
  return result
}

const getBlockPrefixByDepth = (depth: number) => {
  if (!nestedBulletSymbolsMap[depth]) {
    return `${generateOffset(depth)}▪`
  }
  return `${generateOffset(depth)}${nestedBulletSymbolsMap[depth]}`
}

export const toPlainTextWithBullets = (rawState: RawContentish): string => {
  let text = ''
  let orderedList: string[] = []
  rawState.blocks.forEach((b, i) => {
    if (b.type === 'ordered-list-item') {
      orderedList.push(b.text)
    } else {
      if (orderedList.length !== 0) {
        orderedList.forEach((item, index) => (text += `\n ${index + 1}. ${item}`))
        orderedList = []
      }
      if (b.type === 'unordered-list-item') {
        if (i > 0) {
          text += `\n${getBlockPrefixByDepth(b.depth)} ${b.text}`
        } else {
          text += `${getBlockPrefixByDepth(b.depth)} ${b.text}`
        }
      } else {
        if (i > 0) {
          text += `\n${b.text}`
        } else {
          text += `${b.text}`
        }
      }
    }
  })
  return text
}

/**
 * Previously `convertToRaw(ContentState.createFromText(text))`. draft-js split
 * on /\r\n?|\n/ into unstyled depth-0 blocks; this reproduces that exactly.
 */
const textToBlocks = (text: string): RawContentish => ({
  blocks: text.split(/\r\n?|\n/).map(line => ({ text: line, type: 'unstyled' as BlockType, depth: 0 })),
})

export const fromPlainTextWithBullets = (text: string): RawContentish => {
  const rawState = textToBlocks(text)
  rawState.blocks.forEach(b => {
    const trimmedText = b.text.trim()
    if (trimmedText.length > 0 && b.text.startsWith('  ') && bulletSymbols.includes(trimmedText[0])) {
      // it's a nested bullet! find out the depth
      let depth = 1
      for (let i = 1; i < b.text.length; i++) {
        if (b.text[i] === ' ') {
          depth += 1
        } else {
          break
        }
      }
      b.depth = depth / 2
    }
    if (trimmedText.length > 0 && bulletSymbols.includes(trimmedText[0])) {
      const bulletSymbol = trimmedText[0]
      // usually bullets are followed by a space. since we're removing the original bullet (to be replaced by the
      // editor's own bullet), let's get rid of the space too
      if (trimmedText.length > 1 && trimmedText[1] === ' ') {
        b.text = trimmedText.replace(`${bulletSymbol} `, '')
      } else {
        b.text = trimmedText.replace(bulletSymbol, '')
      }
      b.type = 'unordered-list-item'
    }
  })
  return rawState
}
